/**
 * code-verification.js
 *
 * Responsible for everything on the confirmation-code slide (slide 2):
 *  - Generating and logging a random 6-digit verification code.
 *  - Input UX: digit-only enforcement, auto-focus next/prev, paste handling,
 *    select-all on focus.
 *  - Resetting the slide (clear inputs + invalid state) on arrival.
 *  - Validating the entered code when the confirm button is clicked.
 *  - On successful code match: calling the API and handling the response.
 *  - Regenerating a fresh code when the resend button is clicked.
 */

import { clearFormData, clearStepPersistence, saveCurrentStep } from './form-persistence';
import { toggleLoader } from './form-fields';

/**
 * Set the status message on the code slide.
 *
 * @param {HTMLFormElement} form
 * @param {string}          message
 * @param {'error'|'success'|''} type
 */
function setStatusMsg(form, message, type = '') {
	const slide2StatusMsg = form.querySelector(
		'.swiper-slide:nth-child(2) .cr-form__status-msg'
	);
	if (!slide2StatusMsg) return;

	slide2StatusMsg.textContent = message;
	slide2StatusMsg.className = 'cr-form__status-msg';

	if (type === 'error') {
		slide2StatusMsg.classList.add('cr-form__status-msg--error');
	} else if (type === 'success') {
		slide2StatusMsg.classList.add('cr-form__status-msg--success');
	}
}

/**
 * Reset the code slide to its initial state:
 * clear all inputs, remove the invalid class, focus the first input, clear status.
 *
 * @param {HTMLFormElement}             form
 * @param {NodeListOf<HTMLInputElement>} codeInputs
 * @param {HTMLElement}                 wrapper
 */
function resetCodeSlide(form, codeInputs, wrapper) {
	codeInputs.forEach((input) => (input.value = ''));
	wrapper?.classList.remove('invalid');
	setStatusMsg(form, '', '');
	codeInputs[0]?.focus();
}

/**
 * Call the API with the collected form data and handle success / error.
 *
 * @param {HTMLFormElement}             form
 * @param {Record<string, HTMLElement>} elements
 * @param {import('./api').default}     api
 * @param {HTMLButtonElement}           confirmBtn
 * @param {import('swiper').default}    swiper
 */
async function submitToAPI(form, elements, api, confirmBtn, swiper) {
	const translations = window.crTranslations || {};

	confirmBtn.disabled = true;
	toggleLoader(form, true);

	try {
		const data = {
			'card-number': elements.cardNumber.value.replace(/\D/g, ''),
			name: elements.name.value.trim(),
			birthday: elements.birthDate.value.replace(/\D/g, ''),
			city: elements.city.value.trim(),
			phone: '370' + elements.phone.value.replace(/\s/g, ''),
			email: elements.email.value.trim(),

			'email-send': elements.agreeEmail.checked,
			'sms-send': elements.agreeSms.checked,
			agreement: elements.agreeTerms.checked,
		};

		setStatusMsg(form, '', '');
		const response = await api.register(data);
		const isSuccess = response.body.Status === 'OK';

		if (!isSuccess) {
			console.error(response.body.ErrorText);
			setStatusMsg(
				form,
				response.body.ErrorText || 'Registration failed',
				'error'
			);
			return;
		}

		// Success: clear saved draft
		clearFormData();
		console.log('[Card Registration] Registration successful.');

		const event = new CustomEvent('cardRegistrationSuccess', {
			bubbles: true,
			detail: {
				response: response.body,
				formId: form.id || form.dataset.formId,
			},
		});
		form.dispatchEvent(event);
	} catch (err) {
		console.error('[Card Registration] Error:', err.message);
		setStatusMsg(
			form,
			err.message || 'Įvyko klaida. Bandykite vėliau.',
			'error'
		);
	} finally {
		confirmBtn.disabled = false;
		confirmBtn.classList.remove('cr-confirm-btn--loading');
		toggleLoader(form, false);
	}
}

/**
 * Start a cooldown timer for the resend button.
 *
 * @param {HTMLButtonElement} resendBtn
 */
function startResendCooldown(resendBtn) {
	const btnText = resendBtn.querySelector('.button-text');
	const originalText = btnText?.textContent || 'Siųsti dar kartą';
	let seconds = 30;

	resendBtn.disabled = true;

	const updateButton = () => {
		if (btnText) {
			btnText.textContent = `${originalText} (${seconds}s)`;
		}
	};

	updateButton();

	const interval = setInterval(() => {
		seconds--;
		if (seconds <= 0) {
			clearInterval(interval);
			resendBtn.disabled = false;
			if (btnText) btnText.textContent = originalText;
		} else {
			updateButton();
		}
	}, 1000);
}

/**
 * Initialise the 6-digit verification code logic for a single form.
 *
 * @param {HTMLFormElement}             form
 * @param {import('swiper').default}    swiper     - The Swiper instance (may be null).
 * @param {import('./api').default}     api        - CardRegistrationAPI instance.
 * @param {Record<string, HTMLElement>} elements   - Field name → DOM element map.
 */
export function initCodeVerification(form, swiper, api, elements) {
	const codeInputs = form.querySelectorAll('.cr-code-input');
	const codeInputsWrapper = form.querySelector('.cr-code-inputs');
	const confirmBtn = form.querySelector('.cr-confirm-btn');
	const resendBtn = form.querySelector('.cr-resend-btn');

	if (!codeInputs.length) return;

	// ── Input UX ────────────────────────────────────────────────────────────
	codeInputs.forEach((input, idx) => {
		// Digit-only + auto-advance + clear invalid state
		input.addEventListener('input', (e) => {
			e.target.value = e.target.value.replace(/\D/g, '');
			codeInputsWrapper?.classList.remove('invalid');

			if (e.target.value && idx < codeInputs.length - 1) {
				codeInputs[idx + 1].focus();
			}
		});

		// Select all text on focus so the user can immediately overtype
		input.addEventListener('focus', (e) => {
			e.target.select();
		});

		// Backspace on empty input → move to previous
		input.addEventListener('keydown', (e) => {
			if (e.key === 'Backspace' && !e.target.value && idx > 0) {
				codeInputs[idx - 1].focus();
			}
		});

		// Paste: distribute digits across inputs
		input.addEventListener('paste', (e) => {
			e.preventDefault();
			const digits = e.clipboardData
				.getData('text')
				.substring(0, 6)
				.replace(/\D/g, '')
				.split('');

			codeInputsWrapper?.classList.remove('invalid');

			digits.forEach((digit, dIdx) => {
				if (codeInputs[dIdx]) codeInputs[dIdx].value = digit;
			});

			if (digits.length > 0) {
				codeInputs[
					Math.min(digits.length, codeInputs.length - 1)
				].focus();
			}
		});
	});

	// Clear status message when user starts typing or interacting
	codeInputsWrapper?.addEventListener('input', () => {
		setStatusMsg(form, '', '');
		codeInputsWrapper.classList.remove('invalid');
	});

	// ── When user arrives on slide 2 ──────────────────────────
	if (swiper) {
		swiper.on('slideChange', () => {
			if (swiper.activeIndex === 1) {
				resetCodeSlide(form, codeInputs, codeInputsWrapper);
			}
		});
	}

	// ── Resend: reset slide and request fresh code ──────────────────────────
	resendBtn?.addEventListener('click', async () => {
		if (swiper && swiper.activeIndex !== 1) return;
		resetCodeSlide(form, codeInputs, codeInputsWrapper);
		toggleLoader(form, true);
		resendBtn.disabled = true;

		try {
			const phone = '370' + elements.phone.value.replace(/\s/g, '');
			const name = elements.name.value.trim();
			await api.sendCode(phone, name);
			startResendCooldown(resendBtn);
			setStatusMsg(form, '', '');
		} catch (err) {
			console.error('[Card Registration] Resend failed:', err.message);
			setStatusMsg(
				form,
				err.message || 'Įvyko klaida. Bandykite vėliau.',
				'error'
			);
			resendBtn.disabled = false;
		} finally {
			toggleLoader(form, false);
		}
	});

	// ── Confirm (PATVIRTINTI): validate code → submit to API ─────────────────
	confirmBtn?.addEventListener('click', async () => {
		if (swiper && swiper.activeIndex !== 1) return;
		const entered = Array.from(codeInputs)
			.map((input) => input.value)
			.join('');

		const translations = window.crTranslations || {};

		if (entered.length < 6) {
			codeInputsWrapper?.classList.add('invalid');
			setStatusMsg(
				form,
				translations.error_invalid_code || 'Invalid code.',
				'error'
			);
			return;
		}

		setStatusMsg(form, '', '');
		toggleLoader(form, true);
		confirmBtn.disabled = true;

		try {
			const phone = '370' + elements.phone.value.replace(/\s/g, '');
			await api.verifyCode(entered, phone);
			codeInputsWrapper?.classList.remove('invalid');
			submitToAPI(form, elements, api, confirmBtn, swiper);
		} catch (err) {
			console.error(
				'[Card Registration] Verification failed:',
				err.message
			);
			codeInputsWrapper?.classList.add('invalid');
			setStatusMsg(
				form,
				translations.error_invalid_code || 'Invalid code.',
				'error'
			);
			confirmBtn.disabled = false;
			toggleLoader(form, false);
		}
	});
}
